import { useState } from 'react';
import {
  Bell,
  Send,
  Users,
  Truck,
  User,
  Globe,
  CheckCircle2,
  Loader2,
  Smartphone,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { useBroadcastNotification } from '../hooks/usePushNotifications';
import type { NotificationTarget } from '../api/push-notifications.api';
import { cn } from '@/lib/utils';

const TARGETS: {
  value: NotificationTarget;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bg: string;
}[] = [
  {
    value: 'all',
    label: 'All Users',
    description: 'Every active customer and driver',
    icon: Globe,
    color: 'text-violet-600',
    bg: 'bg-violet-50 border-violet-200',
  },
  {
    value: 'drivers',
    label: 'All Drivers',
    description: 'Every registered driver on the platform',
    icon: Truck,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
  },
  {
    value: 'customers',
    label: 'All Customers',
    description: 'Every registered customer',
    icon: Users,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200',
  },
  {
    value: 'user',
    label: 'Specific User',
    description: 'One individual by their User ID',
    icon: User,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
  },
];

const QUICK_TEMPLATES = [
  {
    label: 'Maintenance',
    title: 'Scheduled Maintenance',
    body: 'We will be down for maintenance from 2 AM – 3 AM. Sorry for the inconvenience.',
  },
  {
    label: 'New Feature',
    title: '🚀 New Feature Available',
    body: 'Check out the latest update — we have added exciting new features for you!',
  },
  {
    label: 'Offer',
    title: '🎁 Special Offer',
    body: 'Use code ZIPTO20 for 20% off your next delivery. Valid today only!',
  },
  {
    label: 'Driver Bonus',
    title: '💰 Bonus Opportunity',
    body: 'Complete 5 trips today and earn an extra ₹200 bonus. Go online now!',
  },
];

interface SendResult {
  target: string;
  fcm: { success: number; failure: number };
  inApp: number;
  sentAt: Date;
  title: string;
}

export function PushNotificationsPage() {
  const [target, setTarget] = useState<NotificationTarget>('drivers');
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [history, setHistory] = useState<SendResult[]>([]);

  const { mutate: broadcast, isPending } = useBroadcastNotification();

  const isValid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (target !== 'user' || userId.trim().length > 0);

  const handleSend = () => {
    if (!isValid || isPending) return;
    broadcast(
      {
        target,
        title: title.trim(),
        body: body.trim(),
        ...(target === 'user' && userId.trim() ? { user_id: userId.trim() } : {}),
      },
      {
        onSuccess: (data) => {
          setHistory((prev) => [
            { ...data, sentAt: new Date(), title: title.trim() },
            ...prev.slice(0, 9),
          ]);
          setTitle('');
          setBody('');
          setUserId('');
        },
      },
    );
  };

  const applyTemplate = (t: (typeof QUICK_TEMPLATES)[0]) => {
    setTitle(t.title);
    setBody(t.body);
  };

  const selectedTarget = TARGETS.find((t) => t.value === target)!;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Push Notifications"
        description="Send real-time push notifications to users via FCM and in-app inbox"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ── Compose Panel ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Target Selection */}
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-semibold text-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Select Audience
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {TARGETS.map((t) => {
                const Icon = t.icon;
                const active = target === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setTarget(t.value)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                      active
                        ? `${t.bg} border-current ${t.color}`
                        : 'border-border bg-background hover:bg-muted',
                    )}
                  >
                    <Icon
                      className={cn(
                        'mt-0.5 h-5 w-5 shrink-0',
                        active ? t.color : 'text-muted-foreground',
                      )}
                    />
                    <div>
                      <p
                        className={cn(
                          'text-sm font-semibold',
                          active ? t.color : 'text-foreground',
                        )}
                      >
                        {t.label}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {target === 'user' && (
              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-foreground">
                  User ID <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Paste the user's UUID here"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
          </div>

          {/* Quick Templates */}
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              Quick Templates
            </h3>
            <div className="flex flex-wrap gap-2">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  onClick={() => applyTemplate(t)}
                  className="rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message Compose */}
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-semibold text-foreground flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Compose Message
            </h3>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-foreground">
                  Title <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Scheduled Maintenance"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={65}
                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  {title.length}/65
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-foreground">
                  Message Body <span className="text-destructive">*</span>
                </label>
                <textarea
                  placeholder="Write your notification message here…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  maxLength={240}
                  className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  {body.length}/240
                </p>
              </div>
            </div>

            {/* Preview */}
            {(title || body) && (
              <div className="mt-4 rounded-lg border border-dashed bg-muted/30 p-4">
                <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Smartphone className="h-3.5 w-3.5" />
                  Notification Preview
                </p>
                <div className="rounded-lg bg-white dark:bg-zinc-900 px-4 py-3 shadow-sm border">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-white text-xs font-bold shrink-0">
                      Z
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground leading-snug">
                        {title || 'Notification Title'}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
                        {body || 'Message body will appear here…'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Sending to:{' '}
                <span className={cn('font-semibold', selectedTarget.color)}>
                  {selectedTarget.label}
                </span>
              </p>
              <Button
                onClick={handleSend}
                disabled={!isValid || isPending}
                className="gap-2"
              >
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {isPending ? 'Sending…' : 'Send Notification'}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-5">
          {/* Info cards */}
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              How it works
            </h3>
            <div className="space-y-3">
              {[
                {
                  icon: Smartphone,
                  title: 'FCM Push',
                  desc: 'Delivered even when the app is closed, via Firebase Cloud Messaging',
                  color: 'text-blue-500',
                },
                {
                  icon: Bell,
                  title: 'In-App Inbox',
                  desc: 'Also stored in the user\'s notification inbox for later reading',
                  color: 'text-violet-500',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-3">
                  <item.icon
                    className={cn('mt-0.5 h-4 w-4 shrink-0', item.color)}
                  />
                  <div>
                    <p className="text-xs font-semibold text-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-700 font-medium">
                Firebase Setup Required
              </p>
              <p className="mt-0.5 text-xs text-amber-600">
                Set{' '}
                <code className="font-mono bg-amber-100 px-1 rounded">
                  FIREBASE_PROJECT_ID
                </code>
                ,{' '}
                <code className="font-mono bg-amber-100 px-1 rounded">
                  FIREBASE_CLIENT_EMAIL
                </code>{' '}
                &amp;{' '}
                <code className="font-mono bg-amber-100 px-1 rounded">
                  FIREBASE_PRIVATE_KEY
                </code>{' '}
                in the backend <code className="font-mono bg-amber-100 px-1 rounded">.env</code> to
                enable FCM delivery.
              </p>
            </div>
          </div>

          {/* Send History */}
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              Recent Sends
            </h3>
            {history.length === 0 ? (
              <div className="flex flex-col items-center py-6 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-xs text-muted-foreground">
                  No notifications sent yet this session
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border bg-background p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-foreground truncate max-w-[160px]">
                        {item.title}
                      </p>
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="capitalize">{item.target}</span>
                      <span>·</span>
                      <span>FCM {item.fcm.success}</span>
                      <span>·</span>
                      <span>{item.sentAt.toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
